import { ROUTES } from "../../common/constants/routes";
import { checkPermissions } from "../../common/utils/checkPermissions";

interface IPermission {
  read: boolean;
  create: boolean;
  delete: boolean;
  edit: boolean;
  customerApplication: string;
  name: string;
  permissionId: string;
}

type GoogleLandingRoute = {
  permissionNames?: string[];
  route: string;
  requiresPermission?: boolean;
  requiresAll?: boolean;
  requiresAdmin?: boolean;
  requiresVendorManager?: boolean;
};

const GOOGLE_LANDING_ROUTES: GoogleLandingRoute[] = [
  { permissionNames: ["Projects"], route: ROUTES.GOOGLE_PROJECTS, requiresPermission: true },
  { permissionNames: ["Manual Projects"], route: ROUTES.GOOGLE_MANUAL_PROJECTS, requiresPermission: true },
  { permissionNames: ["Projects"], route: ROUTES.GOOGLE_INVOICING, requiresPermission: true },
  { route: ROUTES.GOOGLE_LINGUIST_MANAGEMENT, requiresAdmin: true, requiresVendorManager: true },
  { permissionNames: ["Projects", "Extension Requests"], route: ROUTES.GOOGLE_EXTENSION_REQUESTS, requiresPermission: true, requiresAll: true },
  { permissionNames: ["Queries"], route: ROUTES.GOOGLE_QUERIES, requiresPermission: true },
];

/**
 * Gets the first accessible Google landing route based on user permissions and navigation hierarchy.
 * Follows the order of routes in the left menu.
 * Priority: Projects → Manual Projects → Invoicing → Linguist Management → Extension Requests → Queries → Main
 * Extension Requests requires both Projects and Extension Requests access together.
 *
 * @param userPermissions - Array of user permissions
 * @param userDetails - Optional user details used for admin-only routes such as linguist management
 * @returns The route string for the first accessible page
 */
export const getFirstAccessibleGoogleRoute = (
  userPermissions: IPermission[],
  userDetails?: { admin?: boolean; vendorManager?: boolean }
): string => {
  return (
    GOOGLE_LANDING_ROUTES.find(({ permissionNames, requiresPermission, requiresAll, requiresAdmin, requiresVendorManager }) => {
      if (requiresAdmin || requiresVendorManager) {
        const isAdmin = !!userDetails?.admin;
        const isVendorManager = !!userDetails?.vendorManager;

        if (requiresAdmin && requiresVendorManager) {
          return isAdmin || isVendorManager;
        }

        if (requiresAdmin) return isAdmin;
        if (requiresVendorManager) return isVendorManager;
      }

      if (!requiresPermission || !permissionNames) return false;

      const matches = permissionNames.map((permissionName) =>
        checkPermissions(userPermissions, [
          { customerApplication: "Google", permissionName, action: "read" },
        ])
      );
      return requiresAll ? matches.every(Boolean) : matches.some(Boolean);
    })?.route ?? ROUTES.GOOGLE_MAIN
  );
};
